/**
 * Centralized permission utilities. Use these functions for all permission checks
 * across the app (routes, Sidebar, components) to ensure consistent matching logic.
 */

/**
 * Match mode constants.
 */
export const MATCH_MODE_ALL = 'all';
export const MATCH_MODE_ANY = 'any';
export type MatchMode = typeof MATCH_MODE_ALL | typeof MATCH_MODE_ANY;

export interface RoutePermissionSet {
  matchMode: MatchMode; // This match mode allow us to changed the logic comparing for the allow permissions.
  allow: string[];
  deny?: string[]; // deny permissions are always managed as a "any" matchMode.
  redirectOnDeny?: (pathname: string, search: string) => string;
  includedCountryCodes?: string[];
}

export const isCountryIncluded = (
  countryCode: string | undefined,
  includedCountryCodes: string[] | undefined
): boolean => {
  if (!includedCountryCodes?.length) return true;
  if (!countryCode) return false;
  const normalizedCountryCode = countryCode.toUpperCase();
  return includedCountryCodes.some((code) => code.toUpperCase() === normalizedCountryCode);
};

/**
 * Checks if user has required permissions. Mirrors backend hasPermission logic (rbac.ts).
 * Single pass over requiredPermissions; matchMode selects all vs any.
 *
 * @param userPermissions Array in 'resource.action.scope' format
 * @param requiredPermissions Array in 'resource.action' format
 * @param matchMode 'all' = every permission required, 'any' = at least one
 */
export const hasRequiredPermissions = (
  userPermissions: string[] | undefined,
  requiredPermissions: string[],
  matchMode: MatchMode
): boolean => {
  if (!userPermissions) return false;
  if (!requiredPermissions || requiredPermissions.length === 0) return true;

  const matches = (p: string) => userPermissions.some((up) => up === p || up.startsWith(`${p}.`));

  const { hasAll, hasAny } = requiredPermissions.reduce<{ hasAll: boolean; hasAny: boolean }>(
    (acc, p) => {
      const match = matches(p);
      return { hasAll: acc.hasAll && match, hasAny: acc.hasAny || match };
    },
    { hasAll: true, hasAny: false }
  );
  return matchMode === MATCH_MODE_ANY ? hasAny : hasAll;
};

/**
 * Checks if a user has access based on a RoutePermissionSet (allow/deny/matchMode).
 * The matchMode is used to determine if the user has all or any of the permissions in the allow array,
 * it does not affect the deny permissions.
 * Deny has priority over allow, and is always managed as a "any" matchMode.
 */
export const hasAccess = (userPermissions: string[] | undefined, rule: RoutePermissionSet): boolean => {
  if (!userPermissions) return false;

  if (rule.deny && rule.deny.length > 0 && hasRequiredPermissions(userPermissions, rule.deny, MATCH_MODE_ANY)) {
    return false;
  }
  return hasRequiredPermissions(userPermissions, rule.allow, rule.matchMode);
};

export const isDenied = (userPermissions: string[] | undefined, deny: string[] | undefined): boolean => {
  if (!deny || deny.length === 0) return false;
  return hasRequiredPermissions(userPermissions, deny, MATCH_MODE_ANY);
};

export const canAccessWithAdminBypass = (
  params: {
    isAdmin: boolean;
    userPermissions: string[] | undefined;
    countryCode?: string;
  },
  rule: RoutePermissionSet
): boolean => {
  if (!isCountryIncluded(params.countryCode, rule.includedCountryCodes)) {
    return false;
  }
  if (params.isAdmin) {
    return !isDenied(params.userPermissions, rule.deny);
  }
  return hasAccess(params.userPermissions, rule);
};
